import { useQuery as useApolloQuery } from '@apollo/client'
import { GET_PLINKO_TICKETS_LEADERBOARD, useGetUserProfileByAddress } from '@/api/query'
import { GetUserLootboxCompetitionLeaderboardQuery } from '@/__generated__/graphql'

import PodiumGold from '@/public/assets/images/leaderboard/podium-gold.png'
import PodiumSilver from '@/public/assets/images/leaderboard/podium-silver.png'
import PodiumBronze from '@/public/assets/images/leaderboard/podium-bronze.png'
import { AvatarCircle } from '@/components/atoms/Avatar'
import TrophyIcon from '@/public/assets/icons/trophy.svg'
import ConfettiIcon from '@/public/assets/icons/confetti.svg'
import PromoBoxIcon from '@/public/assets/images/leaderboard/promo-box-icon.png'
import Link from 'next/link'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { UserProfile } from '@/components/atoms/UserProfile/UserProfile'
import { useSession } from '@/context/SessionProvider'
import {
  Flex,
  Box,
  SimpleGrid,
  Text,
  Center,
  Spinner,
  FlexProps,
  SkeletonCircle,
  Skeleton,
  Grid,
  Button,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react'
import { useEffect } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { GameRules } from 'types/types'
import { Image } from '@/components/HOC/Image'
import { ProgressBar } from '../atoms/ProgressBar'
import { useLocalStorage } from 'usehooks-ts'
import { LocalStorageKey } from '@/constants/localstorage'
import QuestionCircle from '@/public/assets/icons/question-circle.svg'

export const LootboxCompetition = () => {
  const modal = useAppModal()
  const [rulesModal, setRulesModal] = useLocalStorage(LocalStorageKey.CompetitionRulesModal, false)

  useEffect(() => {
    if (!rulesModal) {
      modal.openModalNew(AppModals.GameRules, { game: GameRules.OPENING_COMPETITION })
      setRulesModal(true)
    }
  }, [])

  return <LootboxLeaderboard />
}

const LootboxLeaderboard = () => {
  const { data, loading } = useApolloQuery(GET_PLINKO_TICKETS_LEADERBOARD)

  const leaderboard =
    data?.userCompetitions?.nodes.map((item) => ({ player: item?.player || '', score: item?.score || 0 })) || []

  return (
    <>
      <PlinkoBanner />
      <LootboxCompetitionPodium users={leaderboard.slice(0, 3)} />
      <LootboxCompetitionLeaderboard leaderboard={leaderboard.slice(3, 50)} isLoading={loading} />
    </>
  )
}

const LootboxCompetitionLeaderboard = ({
  leaderboard,
  isLoading,
}: {
  leaderboard: { player: string; score: number }[]
  isLoading: boolean
}) => {
  const { t } = useTranslation()
  const { account } = useSession()
  const userIndex = leaderboard?.findIndex((element) => element.player === account?.injectiveAddress) || -1

  if (isLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    )
  }

  return (
    <Box bg={'ninja.600'} borderRadius={'xl'} overflowX={'auto'}>
      <Box fontSize={'sm'} fontWeight={700} py={6} px={6} borderBottom={'1px'} borderColor={'ninja.500'}>
        <Grid gridTemplateColumns={{ base: '40px  4fr 5fr', md: '40px  4fr 5fr' }} alignItems={'center'}>
          <Text>#</Text>
          <Text>{t('common.user')}</Text>
          <Flex alignItems={'center'} justifyContent={'end'} gap={1}>
            <Text textAlign={'right'}>{t('common.tickets')}</Text>
          </Flex>
        </Grid>
      </Box>

      <Box>
        {!leaderboard?.length ? (
          <Text pt={8} pb={6} textAlign={'center'} color={'ninja.100'}>
            No statistics at this time
          </Text>
        ) : (
          <>
            {leaderboard.map((element, index) => (
              <Box
                px={6}
                py={3}
                borderBottom={'1px'}
                borderColor={index === leaderboard.length - 1 ? 'transparent' : 'ninja.500'}
                bg={index === userIndex ? 'ninja.500' : 'transparent'}
                key={element.player + index}
              >
                <LootboxCompetitionRow index={index + 4} row={element} />
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  )
}

const UserPodiumSkeleton = ({ styles, size = 'xl' }: { styles?: FlexProps; size?: string }) => {
  const sizeMap = {
    xl: '108px',
    lg: '76px',
  }

  return (
    <Flex flexDir={'column'} alignItems={'center'} {...styles}>
      <SkeletonCircle
        opacity={1}
        size={sizeMap[size as keyof typeof sizeMap]}
        startColor={'ninja.500'}
        endColor={'ninja.600'}
        mb={2}
      />
      <Skeleton opacity={1} startColor={'ninja.500'} endColor={'ninja.600'} w={40} h={'38px'} rounded={'2xl'} />
    </Flex>
  )
}

const LootboxCompetitionPodium = ({ users }: { users: { player: string; score: number }[] }) => {
  return (
    <SimpleGrid overflow={'hidden'} alignItems={'end'} columns={{ base: 2, sm: 3 }}>
      <Box order={{ base: 2, sm: 1 }} mt={{ base: -32, sm: 0 }}>
        <PodiumUser
          player={users.at(1)?.player}
          score={users.at(1)?.score}
          rank={2}
          styles={{
            pos: 'relative',
            bottom: { base: -10, md: -12, xl: -20 },
            zIndex: 20,
          }}
        />

        <Image
          left={'50%'}
          pos={{ base: 'relative', sm: 'relative', md: 'static' }}
          transform={{ base: 'translateX(-50%)', md: 'none' }}
          mx={'auto'}
          zIndex={10}
          alt={''}
          maxW={{ base: '260px', md: '100%' }}
          src={PodiumSilver}
        />
      </Box>
      <Box
        zIndex={10}
        pos={'relative'}
        order={{ base: 1, sm: 2 }}
        gridColumnStart={{ base: 1, sm: 2 }}
        gridColumnEnd={{ base: 3, sm: 'auto' }}
      >
        <PodiumUser
          player={users.at(0)?.player}
          score={users.at(0)?.score}
          rank={1}
          styles={{
            pos: 'relative',
            zIndex: 10,
            mb: { base: -16, sm: -14, md: -20, xl: -24 },
          }}
        />
        <Image
          mx={'auto'}
          pos={{ base: 'static', sm: 'relative', md: 'static' }}
          transform={{ base: 'none', sm: 'translateX(-50%)', md: 'none' }}
          left={'50%'}
          maxW={{ base: '240px', md: '100%' }}
          alt={''}
          src={PodiumGold}
        />
      </Box>
      <Box order={{ base: 3, sm: 3 }} mt={{ base: -32, sm: 0 }}>
        <PodiumUser
          player={users.at(2)?.player}
          score={users.at(2)?.score}
          rank={3}
          styles={{
            pos: 'relative',
            bottom: { base: -10, md: -12, xl: -20 },
            zIndex: 10,
          }}
        />
        <Image
          mx={'auto'}
          left={'50%'}
          pos={{ base: 'relative', sm: 'relative', md: 'static' }}
          transform={{ base: 'translateX(-50%)', md: 'none' }}
          alt={''}
          maxW={{ base: '250px', md: '100%' }}
          src={PodiumBronze}
        />
      </Box>
    </SimpleGrid>
  )
}

export const PodiumUser = ({
  player,
  score,
  rank,
  avatarSize = 'xl',
  styles,
}: {
  player?: string
  score?: number
  rank: number
  avatarSize?: any
  styles: FlexProps
}) => {
  const { data } = useGetUserProfileByAddress(player || '')
  const modal = useAppModal()
  const colors = [
    {
      bg: '#FFC14A',
      nicknameColor: '#121236',
      numberColor: '#121236',
    },
    {
      bg: '#E5E5E5',
      nicknameColor: '#121236',
      numberColor: '#121236',
    },
    {
      bg: '#6C56AA',
      nicknameColor: '',
      numberColor: '#ffffff',
    },
  ]

  if (!data) {
    return <UserPodiumSkeleton size={avatarSize} styles={styles} />
  }

  return (
    <Flex flexDir={'column'} alignItems={'center'} {...styles}>
      <Box cursor={'pointer'} onClick={() => modal.openModalNew(AppModals.UserStats, { address: player! })}>
        <AvatarCircle
          size={avatarSize === 'lg' ? { base: 'md', xl: 'lg' } : { base: 'md', md: 'lg', xl: 'xl' }}
          offset={6}
          src={data?.user?.profileAvatar}
        />
      </Box>
      <Flex
        alignItems={'center'}
        mt={2}
        color={rank === 3 ? 'white' : 'ninja.500'}
        bg={colors[rank - 1].bg}
        fontWeight={700}
        fontSize={avatarSize === 'lg' ? { base: 'xs', xl: 'xl' } : { base: 'md', md: 'lg', xl: 'xl' }}
        rounded={'xl'}
        cursor={'pointer'}
        onClick={() => modal.openModalNew(AppModals.UserStats, { address: player! })}
      >
        <Text
          borderRight={'1px solid'}
          borderColor={`${colors[rank - 1].numberColor}56`}
          px={{ base: 1.5, lg: 2 }}
          color={`${colors[rank - 1].numberColor}56`}
          fontSize={avatarSize === 'lg' ? { base: 'xs', xl: 'sm' } : { base: 'md', md: 'lg', xl: 'xl' }}
        >
          #{rank}
        </Text>
        <Flex gap={{ base: 1, xl: 2 }} alignItems={'center'} px={{ base: 1.5, xl: 3 }} py={1}>
          <Text color={colors[rank - 1].nicknameColor}>{data?.user?.profileUserName}</Text>
          {score !== undefined && (
            <>
              <Box alignSelf={'stretch'} w={'1px'} bg={`${colors[rank - 1].numberColor}56`} />
              <Text color={colors[rank - 1].nicknameColor} opacity={0.6}>
                {score.toLocaleString()}
              </Text>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

type LootboxCompetitionUserStats = {
  score: number
  rank: number
  realScore: number
  rewards: {
    inj: number
    usdt: number
  }
}
type LootboxCompetitionLeaderboardRecord = {
  player: string
  gameScore: number
  score: number
  referralScore: number
  isNftOwner: boolean
  nfts: number
  rewards: {
    inj: number
    usdt: number
  }
}

export const LootboxCompetitionFreeLootbox = ({ score }: { score: number }) => {
  return (
    <Flex
      gridColumnStart={{ base: 'auto', md: 1, xl: 'auto' }}
      gridColumnEnd={{ base: 'auto', md: 3, xl: 'auto' }}
      p={4}
      gap={5}
    >
      <Link href={'/lootbox/inj1quay03xd82t8t25amy0j0l2nycdm3wdemewcuy'}>
        <Flex
          flexShrink={0}
          bg={'ninja.500'}
          rounded={'2xl'}
          w={20}
          h={20}
          justifyContent={'center'}
          alignItems={'center'}
          boxShadow={`0px 0px 8px 0px rgba(255, 255, 255, 0.60) inset, 0px 0px 16px 0px #C853FF inset;`}
        >
          <Image alt={''} mb={2} src={PromoBoxIcon} />
        </Flex>
      </Link>
      <Box fontWeight={700} flexBasis={'full'}>
        <Text mt={1} fontSize={'xs'} color={'ninja.200'}>
          <Text lineHeight={1} mr={1} as={'span'} fontSize={'32px'} color={'white'}>
            {Math.min(1000, score)}
          </Text>
          {/* /{score < 1000 ? 1000 : 10000} */}/{1000}
        </Text>
        <Text mb={1} fontSize={'sm'} color={'ninja.100'}>
          {/* {score >= 10000
            ? 'Congrats! You received free boxes!'
            : `Points to receive ${score < 1000 ? 'Free Box' : '10 Free Boxes'}`} */}
          {score >= 1000 ? 'Congrats! You received free boxes!' : `Points to receive a Free Box`}
        </Text>
        <ProgressBar
          minW="none"
          showTexts={false}
          // totalCount={Math.max(0, (score < 1000 ? 1000 : 10000) - score)}
          // totalLimit={score < 1000 ? 1000 : 10000}
          totalLimit={1000}
          totalCount={Math.max(0, 1000 - score)}
        />
      </Box>
    </Flex>
  )
}

const formatScore = (score: number) => {
  if (score < 1000) {
    return score
  }

  return Math.floor(score / 1000).toString() + '.' + Math.floor((score / 100) % 10).toString() + 'k'
}

const LootboxCompetitionUserStats = ({
  data,
  isLoading,
}: {
  data: LootboxCompetitionUserStats
  isLoading: boolean
}) => {
  const { t } = useTranslation()
  if (isLoading) {
    return (
      <SimpleGrid mb={6} columns={{ base: 1, md: 2, xl: 3 }} gap={4}>
        <Skeleton
          gridColumnStart={{ base: 'auto', md: 1, xl: 'auto' }}
          gridColumnEnd={{ base: 'auto', md: 3, xl: 'auto' }}
          h={'112px'}
          rounded={'2xl'}
          startColor={'ninja.500'}
          endColor={'ninja.600'}
        />
        <Skeleton h={'112px'} rounded={'2xl'} startColor={'ninja.500'} endColor={'ninja.600'} />
        <Skeleton h={'112px'} rounded={'2xl'} startColor={'ninja.500'} endColor={'ninja.600'} />
      </SimpleGrid>
    )
  }

  const days = Math.max(Math.ceil((new Date(OC_FINISH).getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000)), 0)

  return (
    <SimpleGrid mb={6} columns={{ base: 1, md: 2, xl: 3 }} gap={4}>
      <Box bg={'ninja.600'} rounded={'2xl'}>
        <LootboxCompetitionFreeLootbox score={data.realScore} />
      </Box>
      <Flex p={4} bg={'ninja.600'} rounded={'2xl'} gap={5}>
        <Flex
          flexShrink={0}
          bg={'ninja.500'}
          rounded={'2xl'}
          w={20}
          h={20}
          p={1}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Text textAlign={'center'} color={'ninja.100'} fontSize={'xs'} fontWeight={700}>
            <Text
              mb={0.5}
              lineHeight={1}
              display={'block'}
              as={'span'}
              color={'white'}
              fontSize={data.rank >= 1000 ? 'lg' : '2xl'}
            >
              {data.rank === 0 ? '#N/A' : data.rank}
              {data.rank === 1 && 'st'}
              {data.rank === 2 && 'nd'}
              {data.rank === 3 && 'rd'}
              {data.rank > 3 && 'th'}
            </Text>
            <Text lineHeight={1} display={'block'} as={'span'}>
              {formatScore(data.score)} pts
            </Text>
          </Text>
        </Flex>
        <Flex flexDir={'column'} justifyContent={'center'} fontWeight={700}>
          <Flex alignItems={'center'} gap={1}>
            <Text lineHeight={1} color={'ninja.200'} fontSize={'xs'}>
              {t('common.potential-reward')}
            </Text>
            <Popover trigger="hover" placement="top">
              <PopoverTrigger>
                <Button alignItems={'center'} textAlign={'right'} minW={0} height={'auto'} variant={'unstyled'}>
                  <QuestionCircle />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                w={52}
                boxShadow={`0px 20px 47px 0px rgba(0, 0, 0, 0.25);`}
                bg={'ninja.600'}
                border={'none'}
                rounded={'xl'}
              >
                <PopoverBody textAlign={'left'} fontWeight={500} fontSize={'sm'} color={'ninja.100'} p={4}>
                  The reward will start being credited once you earn at least 100 points and appear on the Leaderboard.
                  At the end of the contest time prizes will be automatically distributed.
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Flex>
        </Flex>
      </Flex>
      <Flex p={4} bg={'ninja.600'} rounded={'2xl'} gap={5}>
        <Flex
          flexShrink={0}
          bg={'ninja.500'}
          rounded={'2xl'}
          w={20}
          h={20}
          p={5}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <ConfettiIcon />
        </Flex>
        <Flex gap={4} flexBasis={'full'} alignItems={'center'} justifyContent={'space-between'}>
          <Box fontWeight={700}>
            <Text mb={1} lineHeight={1} fontSize={'sm'}>
              <Text fontSize={'26px'} as={'span'}>
                {Math.max(days, 1)}
              </Text>{' '}
              {days < 2 ? 'day' : 'days'}
            </Text>
            <Text lineHeight={1.2} color={'ninja.100'} fontSize={'sm'}>
              Until competition ends
            </Text>
          </Box>
        </Flex>
      </Flex>
    </SimpleGrid>
  )
}

const LootboxCompetitionRow = ({ row, index }: { row: { player: string; score: number }; index: number }) => {
  const { account } = useSession()

  const isCurrentUser = row.player === account?.injectiveAddress
  const isHighlighted = isCurrentUser || index <= 3

  return (
    <Grid gridTemplateColumns={{ base: '40px  4fr 5fr', md: '40px  4fr 5fr' }} alignItems={'center'}>
      <Text
        fontSize={isHighlighted ? 'md' : 'sm'}
        fontWeight={isHighlighted ? 700 : 400}
        color={isHighlighted ? 'white' : 'ninja.100'}
      >
        {index === 0 ? 'N/A' : index}
      </Text>
      <UserProfile address={row.player} />
      <Flex justifyContent={'end'}>
        <Text
          fontSize={isHighlighted ? 'md' : 'sm'}
          fontWeight={isHighlighted ? 700 : 400}
          color={isHighlighted ? 'white' : 'ninja.100'}
        >
          {row.score}
        </Text>
      </Flex>
    </Grid>
  )
}

import { OC_FINISH } from '@/constants/constants'
import { RunesBanner } from '../../Runes/ContestBanner'
import { PlinkoBanner } from '../../Plinko/molecules/ContestBanner'

const convertRankToUSDT = (rank: number) => {
  if (rank == 1) {
    return 1000
  }
  if (rank == 2) {
    return 500
  }
  if (rank == 3) {
    return 250
  }
  if (rank <= 10) {
    return 50
  }
  if (rank <= 30) {
    return 25
  }
  if (rank <= 50) {
    return 10
  }

  return 0
}

export const transformLootboxCompetitionData = (data?: GetUserLootboxCompetitionLeaderboardQuery) => {
  if (!data) {
    return {
      user: { score: 0, rank: 0, rewards: { inj: 0, usdt: 0 }, realScore: 0 },
      leaderboard: [],
    }
  }

  const user = data.user?.nodes.at(0)
  return {
    user: user
      ? {
          realScore: (Number(data.userEntity!.score) + Number(data.userEntity!.refererScore)) * (user.hasNft ? 1 : 1),
          score: user.fullScore as number,
          rank: user.rank as number,
          rewards: {
            inj: user.fullScore >= 100 ? 500 * (user.fullScore / user.totalScore) : 0,
            usdt: convertRankToUSDT(user.rank),
          },
        }
      : { realScore: 0, score: 0, rank: 0, rewards: { inj: 0, usdt: 0 } },
    leaderboard:
      data.leaderboard?.nodes.map((record) => ({
        player: record?.player || '',
        gameScore: (record?.score || 0) as number,
        score: (record?.fullScore || 0) as number,
        referralScore: (record?.refererScore || 0) as number,
        isNftOwner: !!record?.hasNft,
        nfts: record?.nfts ? parseInt(record.nfts) : 0,
        rewards: record
          ? {
              inj: record.fullScore >= 100 ? 500 * (record.fullScore / record.totalScore) : 0,
              usdt: convertRankToUSDT(record.rank),
            }
          : { inj: 0, usdt: 0 },
      })) || [],
  }
}
